import { Board, NoteType } from './types';

export interface BoardTemplate {
    id: string;
    title: string;
    description: string;
    icon: string;
    color: string;
    createBoard: () => Board;
}

export const BOARD_TEMPLATES: BoardTemplate[] = [
    {
        id: 'project-planning',
        title: 'Project Planning',
        description: 'Organize tasks, track progress, and manage timelines.',
        icon: '📋',
        color: 'blue',
        createBoard: () => ({
            id: Math.random().toString(36).substr(2, 9),
            title: 'Project Alpha Planning',
            description: 'Q1 Roadmap and Execution Plan',
            lastEdited: 'Just now',
            themeColor: 'blue',
            isFavorite: false,
            isDeleted: false,
            notes: [
                {
                    id: 't1',
                    type: NoteType.TEXT,
                    x: 100,
                    y: 100,
                    title: 'Project Goals',
                    content: '1. Launch MVP\n2. User Feedback Loop\n3. Market Analysis',
                    colorTheme: 'blue',
                    width: 300,
                    height: 200
                },
                {
                    id: 't2',
                    type: NoteType.TODO,
                    x: 450,
                    y: 100,
                    title: 'To Do',
                    content: '',
                    colorTheme: 'orange',
                    width: 300,
                    height: 400
                },
                {
                    id: 't3',
                    type: NoteType.LIST,
                    x: 800,
                    y: 100,
                    title: 'In Progress',
                    items: [
                        { text: 'Database Schema Design', done: false },
                        { text: 'API Authentication', done: true }
                    ],
                    colorTheme: 'yellow',
                    width: 300,
                    height: 400
                }
            ],
            edges: []
        })
    },
    {
        id: 'design-moodboard',
        title: 'Design Mood Board',
        description: 'Collect inspiration, colors, and visuals for your next design.',
        icon: '🎨',
        color: 'purple',
        createBoard: () => ({
            id: Math.random().toString(36).substr(2, 9),
            title: 'UI/UX Inspiration',
            description: 'Modern Minimalist Aesthetic',
            lastEdited: 'Just now',
            themeColor: 'purple',
            isFavorite: false,
            isDeleted: false,
            notes: [
                {
                    id: 'm1',
                    type: NoteType.TEXT,
                    x: 100,
                    y: 100,
                    title: 'Color Palette',
                    content: '#FF5733 - Accent\n#333333 - Text\n#F9F9F9 - Background',
                    colorTheme: 'white',
                    width: 250,
                    height: 200
                },
                {
                    id: 'm2',
                    type: NoteType.IMAGE,
                    x: 400,
                    y: 80,
                    title: 'Hero Section Idea',
                    imageUrl: 'https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?auto=format&fit=crop&w=600&q=80',
                    colorTheme: 'white',
                    width: 400,
                    height: 300
                },
                {
                    id: 'm3',
                    type: NoteType.STICKER,
                    x: 200,
                    y: 400,
                    url: '/stickers/star.png', // Assuming this path exists or will be handled
                    title: 'Key Element',
                    colorTheme: 'white',
                    width: 150,
                    height: 150
                }
            ],
            edges: []
        })
    },
    {
        id: 'weekly-planner',
        title: 'Weekly Planner',
        description: 'Map out your week with daily goals and schedules.',
        icon: '📅',
        color: 'green',
        createBoard: () => ({
            id: Math.random().toString(36).substr(2, 9),
            title: 'Weekly Overview',
            description: 'Focus and Productivity',
            lastEdited: 'Just now',
            themeColor: 'green',
            isFavorite: false,
            isDeleted: false,
            notes: [
                {
                    id: 'w1',
                    type: NoteType.PLANNER,
                    x: 100,
                    y: 100,
                    title: 'Monday Schedule',
                    content: 'Start strong!',
                    plannerItems: [
                        { id: '1', time: '09:00', title: 'Team Sync', completed: false },
                        { id: '2', time: '11:00', title: 'Deep Work', completed: false }
                    ],
                    colorTheme: 'white',
                    width: 320,
                    height: 500
                },
                {
                    id: 'w2',
                    type: NoteType.TODO,
                    x: 450,
                    y: 100,
                    title: 'Weekly Goals',
                    content: '',
                    colorTheme: 'blue',
                    width: 300,
                    height: 400
                }
            ],
            edges: []
        })
    },
    {
        id: 'brain-dump',
        title: 'Brain Dump',
        description: 'Unleash your creativity. No structure, just ideas.',
        icon: '💡',
        color: 'orange',
        createBoard: () => ({
            id: Math.random().toString(36).substr(2, 9),
            title: 'My Brain Dump',
            description: 'Random thoughts and big ideas',
            lastEdited: 'Just now',
            themeColor: 'orange',
            isFavorite: false,
            isDeleted: false,
            notes: [
                {
                    id: 'b1',
                    type: NoteType.TEXT,
                    x: 400,
                    y: 300,
                    title: 'Big Idea?',
                    content: 'What if we combined AI with...',
                    colorTheme: 'yellow',
                    width: 250,
                    height: 200
                }
            ],
            edges: []
        })
    },
    {
        id: 'study-notes',
        title: 'Study Notes',
        description: 'Organize research, readings, and key concepts.',
        icon: '📖',
        color: 'teal',
        createBoard: () => ({
            id: Math.random().toString(36).substr(2, 9),
            title: 'Physics 101',
            description: 'Notes for the upcoming exam',
            lastEdited: 'Just now',
            themeColor: 'teal',
            isFavorite: false,
            isDeleted: false,
            notes: [
                {
                    id: 's1',
                    type: NoteType.TABLE,
                    x: 100,
                    y: 100,
                    title: ' Formulas',
                    tableData: {
                        headers: ['Concept', 'Formula', 'Notes'],
                        rows: [
                            ['Force', 'F = ma', 'Newton\'s Second Law'],
                            ['Energy', 'E = mc^2', 'Relativity']
                        ]
                    },
                    colorTheme: 'white',
                    width: 500,
                    height: 300
                },
                {
                    id: 's2',
                    type: NoteType.TEXT,
                    x: 100,
                    y: 450,
                    title: 'Key Concepts',
                    content: '- Inertia\n- Momentum\n- Thermodynamics',
                    colorTheme: 'blue',
                    width: 300,
                    height: 250
                }
            ],
            edges: []
        })
    }
];
